import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import OnboardingLayout from '../../components/OnboardingLayout'
import { scrollToFirstFieldError } from '../../lib/onboarding/fieldValidation'
import { ONBOARDING, onboardingChoiceClass } from '../../lib/onboarding/formStyles'
import { ONBOARDING_STEP_COUNT } from '../../lib/onboarding'
import type { OnboardingStepProps } from './stepProps'

const BODY_FAT_ARCHETYPES = [12, 17, 22, 27, 32, 37] as const

const Step4 = ({ onNext, onPrevious, initialData, stepIndex }: OnboardingStepProps) => {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<number | null>((initialData?.body_fat_archetype as number) || null)
  const [error, setError] = useState('')
  const [showInvalid, setShowInvalid] = useState(false)

  const handleSelect = (bf: number) => {
    setSelected(bf)
    setShowInvalid(false)
    setError('')
  }

  const handleNext = () => {
    if (selected === null) {
      setShowInvalid(true)
      setError(t('onboarding.bodyComposition.selectArchetype'))
      scrollToFirstFieldError(['body_fat_archetype'])
      return
    }
    onNext({ body_fat_archetype: selected })
  }

  return (
    <OnboardingLayout step={stepIndex} totalSteps={ONBOARDING_STEP_COUNT} onNext={handleNext} onPrev={onPrevious}>
      <div className="space-y-6">
        <div>
          <h2 className={ONBOARDING.heading}>{t('onboarding.bodyComposition.heading')}</h2>
          <p className={ONBOARDING.subtitle}>{t('onboarding.bodyComposition.subtitle')}</p>
        </div>

        <div
          id="field-body_fat_archetype"
          className={`scroll-mt-32 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 ${
            showInvalid ? 'rounded-2xl ring-2 ring-kilo-danger/60 ring-offset-2 ring-offset-kilo-card' : ''
          }`}
        >
          {BODY_FAT_ARCHETYPES.map((bf) => (
            <button
              key={bf}
              type="button"
              onClick={() => handleSelect(bf)}
              className={`space-y-2 ${onboardingChoiceClass(selected === bf)}`}
            >
              <div className="flex items-baseline gap-1">
                <span className={`text-2xl font-bold ${ONBOARDING.reviewValue}`}>{bf}%</span>
                <span className={`text-sm ${ONBOARDING.muted}`}>{t('onboarding.bodyComposition.bodyFat')}</span>
              </div>
              <p className={`text-sm font-semibold ${ONBOARDING.reviewValue}`}>
                {t(`onboarding.bodyComposition.archetypes.bf${bf}.label`)}
              </p>
              <p className={`text-xs ${ONBOARDING.muted}`}>
                {t(`onboarding.bodyComposition.archetypes.bf${bf}.description`)}
              </p>
            </button>
          ))}
        </div>

        {showInvalid && selected === null ? (
          <p className={`text-sm ${ONBOARDING.muted}`}>
            <span className="text-kilo-danger">*</span> {t('onboarding.bodyComposition.selectArchetype')}
          </p>
        ) : null}

        {error ? <div className={ONBOARDING.error}>{error}</div> : null}
      </div>
    </OnboardingLayout>
  )
}

export default Step4
